/**
 * KTAS 개인 평가결과 API 응답 → TriageEvaluationResultDto 정규화
 *
 * 엔드포인트: GET /{lang}/scenario-ktas/data/{id}
 * 응답: { statusCode, errMsg, data: KtasScenarioResultDto } — 환자 상세는 `data.feedback[]`, 요약은 `data.assessment`.
 * 응답에 없는 내원동기는 `KTAS_VISIT_MOTIVATION_BY_PATIENT_UID` 로 보완한다 (Angular `ktasMotivation` 과 동일).
 */

import type {
	KtasEvaluationSummaryDto,
	KtasFeedbackDto,
	KtasScenarioResultDto,
	TriageEvaluationResultDto,
	TriagePatientDto,
	TriageStatsDto,
} from '@/types/api/scenarioResult';
import { KTAS_VISIT_MOTIVATION_BY_PATIENT_UID } from '@/constants/ktasVisitMotivation';
import { formatSecondsToMMSS } from '@/utils/triageResultMapper';
import { KTAS_EVALUATION_MODE_VALUE } from '@/pages/results/ktasViewPolicy';

/** `gender` 값: 1 = 남성 (Angular `gender === 1`) */
const KTAS_GENDER_MALE = 1;

/** GCS 코드 → 표기 (Angular `ktasGcsTrans` 파이프와 동일) */
const KTAS_GCS_LABEL_BY_CODE: Readonly<Record<number, string>> = {
	1: '3~8',
	2: '9~13',
	3: '13>',
	4: '사정불가',
};

const KTAS_SCORE_PATH_SEPARATOR = ' > ';
const KTAS_COMMENT_SEPARATOR = '\n\n';

function formatGcsLabel(gcsCode: number | null): string {
	if (gcsCode == null) return '';
	return KTAS_GCS_LABEL_BY_CODE[gcsCode] ?? '';
}

function formatKtasLevel(ktasLevel: number | null): string {
	return ktasLevel == null ? '' : String(ktasLevel);
}

function formatBloodPressure(systolic: number, diastolic: number): string {
	return `${systolic}/${diastolic}`;
}

function formatCorrectScorePaths(scorePaths: string[][]): string {
	return scorePaths.map((scorePath) => scorePath.join(KTAS_SCORE_PATH_SEPARATOR)).join('\n');
}

function formatComments(comments: KtasFeedbackDto['comments']): string {
	return [comments.ktas_comment, comments.symptom_comment]
		.filter((comment) => comment.trim() !== '')
		.join(KTAS_COMMENT_SEPARATOR);
}

function mapFeedbackToPatient(feedback: KtasFeedbackDto): TriagePatientDto {
	const { correct_answers: correctAnswers, user_answers: userAnswers } = feedback;
	const correctKtasLevel = formatKtasLevel(correctAnswers.ktas_level);

	return {
		patientName: feedback.name,
		gender: feedback.gender === KTAS_GENDER_MALE,
		age: feedback.age,
		correctAnswer: correctKtasLevel,
		userAnswer: formatKtasLevel(userAnswers.ktas_level),
		isPassed: feedback.pass,
		pastHistory: feedback.medical_history,
		visitMotivation: KTAS_VISIT_MOTIVATION_BY_PATIENT_UID[feedback.p_uid] ?? '',
		bloodPressure: formatBloodPressure(feedback.dia_sbp, feedback.dia_dbp),
		pulse: String(feedback.dia_hr),
		respiration: String(feedback.dia_rr),
		temperature: String(feedback.dia_t),
		spo2: String(feedback.dia_spo2),
		bloodGlucose: String(feedback.dia_bst_min),
		consciousnessLevel: formatGcsLabel(correctAnswers.gcs),
		answerGcs: formatGcsLabel(userAnswers.gcs),
		answerMainSymptom: userAnswers.symptoms.join(', '),
		answerPathway: userAnswers.ktas_score.join(KTAS_SCORE_PATH_SEPARATOR),
		correctGcs: formatGcsLabel(correctAnswers.gcs),
		correctMainSymptom: correctAnswers.symptoms.join(', '),
		correctKtasLevel,
		correctPathway: formatCorrectScorePaths(correctAnswers.ktas_score),
		comments: formatComments(feedback.comments),
	};
}

function calculateCorrectRatePercent(correctCount: number, totalCount: number): number | undefined {
	if (totalCount <= 0) return undefined;
	return Math.round((correctCount / totalCount) * 100);
}

function mapStats(result: KtasScenarioResultDto): TriageStatsDto {
	const totalCount = result.feedback.length;
	const correctCount = result.feedback.filter((feedback) => feedback.pass).length;
	return {
		correctRate: calculateCorrectRatePercent(correctCount, totalCount),
		correctCount,
		totalCount,
		duration: formatSecondsToMMSS(result.play_time / 1000),
		underTriageRate: calculateCorrectRatePercent(result.assessment.under_triage, result.assessment.patients_count),
		overTriageRate: calculateCorrectRatePercent(result.assessment.over_triage, result.assessment.patients_count),
		unclassifiedRate: calculateCorrectRatePercent(result.assessment.untriage, result.assessment.patients_count),
	};
}

function extractKtasResult(apiResponse: unknown): KtasScenarioResultDto | null {
	if (apiResponse == null || typeof apiResponse !== 'object') return null;
	const envelope = apiResponse as { data?: unknown };
	const payload = envelope.data != null && typeof envelope.data === 'object' ? envelope.data : apiResponse;
	const ktasResult = payload as Partial<KtasScenarioResultDto>;
	if (ktasResult.content_code !== 'ktas' || !Array.isArray(ktasResult.feedback)) return null;
	return ktasResult as KtasScenarioResultDto;
}

function extractKtasEvaluationSummary(apiResponse: unknown): KtasEvaluationSummaryDto | null {
	if (apiResponse == null || typeof apiResponse !== 'object') return null;
	const envelope = apiResponse as { data?: unknown };
	const payload = envelope.data != null && typeof envelope.data === 'object' ? envelope.data : apiResponse;
	const summary = payload as Partial<KtasEvaluationSummaryDto> & { feedback?: unknown };
	if (String(summary.mode) !== KTAS_EVALUATION_MODE_VALUE) return null;
	if (Array.isArray(summary.feedback)) return null;
	if (
		typeof summary.play_time !== 'number' ||
		typeof summary.correct_count !== 'number' ||
		typeof summary.patients_count !== 'number'
	) {
		return null;
	}
	return summary as KtasEvaluationSummaryDto;
}

/**
 * KTAS 평가모드(`mode` '3') 요약 응답 정규화 — 정답율·정답수/전체·소요시간만 채우고 환자 목록은 비운다.
 * KTAS 시나리오에서만 호출할 것 (응답에 `content_code` 가 없어 형태로만 판별).
 */
export function normalizeKtasEvaluationSummary(apiResponse: unknown): TriageEvaluationResultDto | null {
	const summary = extractKtasEvaluationSummary(apiResponse);
	if (summary == null) return null;
	return {
		stats: {
			correctRate: calculateCorrectRatePercent(summary.correct_count, summary.patients_count),
			correctCount: summary.correct_count,
			totalCount: summary.patients_count,
			duration: formatSecondsToMMSS(summary.play_time / 1000),
		},
		patients: [],
	};
}

/**
 * KTAS 개인 평가결과 응답 정규화. KTAS 응답(`content_code === 'ktas'` 이고 `feedback` 배열 존재)이 아니면 `null`.
 */
export function normalizeKtasResult(apiResponse: unknown): TriageEvaluationResultDto | null {
	const ktasResult = extractKtasResult(apiResponse);
	if (ktasResult == null) return null;
	return {
		stats: mapStats(ktasResult),
		patients: ktasResult.feedback.map(mapFeedbackToPatient),
	};
}
